import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import api from '../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { HelpTooltip, TitleWithHelp } from '../components/ui/help-tooltip';
import { Ship, Plus, Edit, Trash2, ClipboardList, Clock, Check, X, ChevronDown, ChevronUp, Anchor, DollarSign, Package, Search, Loader2, Paperclip, Copy, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { ViewToggle } from '../components/ui/view-toggle';
import AnexosManager from '../components/AnexosManager';
import AnexosErrorBoundary from '../components/AnexosErrorBoundary';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}` }
});

const formatCurrency = (val) => {
  if (!val && val !== 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
};

const VesselModels = () => {
  const [models, setModels] = useState([]);
  const [processTemplates, setProcessTemplates] = useState([]);
  const [categoriasGlobais, setCategoriasGlobais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [deleteModelId, setDeleteModelId] = useState(null);
  const [activeTab, setActiveTab] = useState('geral');
  const [viewMode, setViewMode] = useState('grid');
  
  // Estado para novo item de checklist
  const [newChecklistItem, setNewChecklistItem] = useState(null);
  const [newCategoria, setNewCategoria] = useState('');
  const [showNewCategoria, setShowNewCategoria] = useState(false);
  
  // Estado para edição de item existente
  const [editingChecklistIndex, setEditingChecklistIndex] = useState(null);

  const [formData, setFormData] = useState({
    codigo: '',
    nome: '',
    descricao: '',
    comprimento: '',
    largura: '',
    capacidade_pessoas: 0,
    capacidade_carga_kg: 0,
    peso_casco_kg: 0,
    horas_estimadas: 0,
    doc_url: '',
    checklist_base: [],
    caracteristicas: [],
    fases_aplicaveis: [],
    config_comercial: {
      preco_base: 0,
      texto_apresentacao: '',
      itens_serie: [],
      opcionais: [],
      upgrades: [],
      dimensoes_gerais: '',
      termos_condicoes: '',
      kit_montagem: '',
      imagens_proposta: [],
    }
  });

  // Busca de produtos para aba comercial
  const [prodSearchTerm, setProdSearchTerm] = useState('');
  const [prodSearchResults, setProdSearchResults] = useState([]);
  const [searchingProd, setSearchingProd] = useState(false);
  const [addingItemType, setAddingItemType] = useState(null); // 'serie' | 'opcional' | 'upgrade'

  useEffect(() => {
    document.title = "Modelos de Embarcação — NautilusOS";
    fetchData();
  }, []);  // eslint-disable-line

  const fetchData = async () => {
    try {
      const [modelsRes, templatesRes, catsRes] = await Promise.all([
        api.get('/vessel-models'),
        api.get('/process-templates'),
        api.get('/categorias-produto')
      ]);
      setModels(modelsRes.data || []);
      setProcessTemplates(templatesRes.data || []);
      setCategoriasGlobais((catsRes.data || []).map(c => c.nome).filter(Boolean));
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Recarrega categorias globais (após criar nova)
  const refetchCategorias = async () => {
    try {
      const res = await api.get('/categorias-produto');
      setCategoriasGlobais((res.data || []).map(c => c.nome).filter(Boolean));
    } catch (e) { /* silent */ }
  };

  // Combina categorias do checklist com as categorias globais (master)
  const categorias = useMemo(() => {
    const cats = new Set();
    categoriasGlobais.forEach(c => cats.add(c));
    formData.checklist_base.forEach(item => {
      if (item.categoria) cats.add(item.categoria);
    });
    if (newChecklistItem?.categoria && !cats.has(newChecklistItem.categoria)) {
      cats.add(newChecklistItem.categoria);
    }
    return Array.from(cats).sort();
  }, [formData.checklist_base, newChecklistItem?.categoria, categoriasGlobais]);

  const resetForm = async () => {
    try {
      const [checklistRes, caracteristicasRes] = await Promise.all([
        api.get('/vessel-models/defaults/checklist'),
        api.get('/vessel-models/defaults/caracteristicas')
      ]);
      
      setFormData({
        codigo: '',
        nome: '',
        descricao: '',
        comprimento: '',
        largura: '',
        capacidade_pessoas: 0,
        capacidade_carga_kg: 0,
        peso_casco_kg: 0,
        horas_estimadas: 0,
        doc_url: '',
        anexos: [],
        checklist_base: (checklistRes.data || []).map(item => ({ ...item, selected: true })),
        caracteristicas: caracteristicasRes.data || [],
        fases_aplicaveis: [],
        config_comercial: { preco_base: 0, texto_apresentacao: '', itens_serie: [], opcionais: [], upgrades: [], dimensoes_gerais: '', termos_condicoes: '', kit_montagem: '', imagens_proposta: [] }
      });
    } catch (error) {
      console.error('Error loading defaults:', error);
      setFormData({
        codigo: '',
        nome: '',
        descricao: '',
        comprimento: '',
        largura: '',
        capacidade_pessoas: 0,
        capacidade_carga_kg: 0,
        peso_casco_kg: 0,
        horas_estimadas: 0,
        doc_url: '',
        anexos: [],
        checklist_base: [],
        caracteristicas: [],
        fases_aplicaveis: [],
        config_comercial: { preco_base: 0, texto_apresentacao: '', itens_serie: [], opcionais: [], upgrades: [], dimensoes_gerais: '', termos_condicoes: '', kit_montagem: '', imagens_proposta: [] }
      });
    }
    setEditingModel(null);
    setNewChecklistItem(null);
    setShowNewCategoria(false);
    setNewCategoria('');
  };

  const handleOpenNew = async () => {
    await resetForm();
    setActiveTab('geral');
    setShowDialog(true);
  };

  const handleEdit = (model) => {
    setEditingModel(model);
    setFormData({
      codigo: model.codigo || '',
      nome: model.nome || '',
      descricao: model.descricao || '',
      comprimento: model.comprimento || '',
      largura: model.largura || '',
      capacidade_pessoas: model.capacidade_pessoas || 0,
      capacidade_carga_kg: model.capacidade_carga_kg || 0,
      peso_casco_kg: model.peso_casco_kg || 0,
      horas_estimadas: model.horas_estimadas || 0,
      doc_url: model.doc_url || '',
      anexos: Array.isArray(model.anexos) ? model.anexos : [],
      checklist_base: (model.checklist_base || []).map(item => ({ ...item, selected: item.selected !== false ? true : false })),
      caracteristicas: model.caracteristicas || [],
      fases_aplicaveis: model.fases_aplicaveis || [],
      config_comercial: {
        preco_base: 0, texto_apresentacao: '', itens_serie: [], opcionais: [], upgrades: [],
        dimensoes_gerais: '', termos_condicoes: '', kit_montagem: '', imagens_proposta: [],
        ...(model.config_comercial || {})
      }
    });
    setNewChecklistItem(null);
    setShowNewCategoria(false);
    setNewCategoria('');
    setActiveTab('geral');
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.codigo.trim()) {
      toast.error('Código é obrigatório');
      return;
    }
    if (!formData.nome.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    // Se há um item novo pendente, adiciona antes de salvar
    let finalChecklist = [...formData.checklist_base].filter(item => item.selected !== false);
    if (newChecklistItem && newChecklistItem.descricao.trim()) {
      finalChecklist = [newChecklistItem, ...finalChecklist];
      finalChecklist.forEach((item, i) => { item.numero = i + 1; });
    }

    try {
      const payload = {
        ...formData,
        checklist_base: finalChecklist,
        capacidade_pessoas: parseInt(formData.capacidade_pessoas) || 0,
        capacidade_carga_kg: parseFloat(formData.capacidade_carga_kg) || 0,
        peso_casco_kg: parseFloat(formData.peso_casco_kg) || 0,
        horas_estimadas: parseFloat(formData.horas_estimadas) || 0,
        config_comercial: formData.config_comercial,
      };

      if (editingModel) {
        await api.put(`/vessel-models/${editingModel.id}`, payload);
        toast.success('Modelo atualizado com sucesso!');
      } else {
        await api.post('/vessel-models', payload);
        toast.success('Modelo criado com sucesso!');
      }

      setShowDialog(false);
      setNewChecklistItem(null);
      fetchData();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar modelo');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/vessel-models/${deleteModelId}`);
      toast.success('Modelo excluído com sucesso!');
      setDeleteModelId(null);
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir modelo');
    }
  };

  const handleDuplicate = async (model) => {
    try {
      const res = await api.post(`/vessel-models/${model.id}/duplicate`, {});
      toast.success(`Modelo duplicado: ${res.data?.nome || 'novo modelo criado'}`);
      await fetchData();
    } catch (error) {
      console.error('Duplicate error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao duplicar modelo');
    }
  };

  // Atualizar item do checklist existente
  const updateChecklistItem = (index, field, value) => {
    const updated = [...formData.checklist_base];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, checklist_base: updated });
  };

  // Iniciar adição de novo item
  const startAddChecklistItem = () => {
    setNewChecklistItem({
      numero: 0,
      categoria: categorias[0] || 'Estrutura',
      descricao: '',
      tipo_item: 'especificacao', // especificacao, produto, servico
      status: false,
      observacao: '',
      is_item_padrao: true // Por padrão, novos itens são adicionados ao padrão
    });
    setShowNewCategoria(false);
    setNewCategoria('');
    setEditingChecklistIndex(null);
  };

  // Confirmar adição do novo item
  const confirmAddChecklistItem = () => {
    if (!newChecklistItem.descricao.trim()) {
      toast.error('Descrição é obrigatória');
      return;
    }
    
    // Adiciona ao final da categoria ou no início da lista
    const updated = [...formData.checklist_base, newChecklistItem];
    // Renumerar
    updated.forEach((item, i) => { item.numero = i + 1; });
    setFormData({ ...formData, checklist_base: updated });
    setNewChecklistItem(null);
    setShowNewCategoria(false);
    setNewCategoria('');
    toast.success('Item adicionado ao checklist!');
  };

  // Cancelar adição
  const cancelAddChecklistItem = () => {
    setNewChecklistItem(null);
    setShowNewCategoria(false);
    setNewCategoria('');
  };

  // Adicionar nova categoria - persiste no master de categorias-produto
  const handleAddNewCategoria = async () => {
    const newCat = newCategoria.trim();
    if (!newCat) {
      toast.error('Digite o nome da categoria');
      return;
    }
    try {
      // Persiste no master (idempotente: backend retorna 400 se ja existe)
      try {
        await api.post(`/categorias-produto`, { nome: newCat, descricao: '' });
      } catch (e) {
        // Se ja existe (400), tudo bem - apenas seguimos
        if (e?.response?.status !== 400) throw e;
      }
      await refetchCategorias();
      setNewChecklistItem(prev => ({ ...prev, categoria: newCat }));
      setShowNewCategoria(false);
      setNewCategoria('');
      toast.success(`Categoria "${newCat}" sincronizada!`);
    } catch (err) {
      console.error('Erro ao criar categoria:', err);
      toast.error('Erro ao criar categoria');
    }
  };

  // Mover item do checklist para cima/baixo (reordenar) - mantido para acessibilidade
  const moveChecklistItem = (originalIndex, direction) => {
    const list = [...formData.checklist_base];
    const target = direction === 'up' ? originalIndex - 1 : originalIndex + 1;
    if (target < 0 || target >= list.length) return;
    [list[originalIndex], list[target]] = [list[target], list[originalIndex]];
    list.forEach((item, i) => { item.numero = i + 1; });
    setFormData({ ...formData, checklist_base: list });
  };

  // Drag-and-drop: reordena de qualquer posicao para qualquer outra
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const handleDragStart = (e, originalIndex) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(originalIndex));
  };
  const handleDragOver = (e, originalIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== originalIndex) setDragOverIndex(originalIndex);
  };
  const handleDragLeave = () => setDragOverIndex(null);
  const handleDrop = (e, toIndex) => {
    e.preventDefault();
    setDragOverIndex(null);
    const fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (Number.isNaN(fromIndex) || fromIndex === toIndex) return;
    const list = [...formData.checklist_base];
    const [moved] = list.splice(fromIndex, 1);
    list.splice(toIndex, 0, moved);
    list.forEach((item, i) => { item.numero = i + 1; });
    setFormData({ ...formData, checklist_base: list });
  };

  // Remover item do checklist
  const removeChecklistItem = (index) => {
    const updated = formData.checklist_base.filter((_, i) => i !== index);
    updated.forEach((item, i) => { item.numero = i + 1; });
    setFormData({ ...formData, checklist_base: updated });
    if (editingChecklistIndex === index) {
      setEditingChecklistIndex(null);
    }
  };

  // Iniciar edição de item
  const startEditChecklistItem = (index) => {
    setEditingChecklistIndex(index);
    setNewChecklistItem(null);
  };

  // Finalizar edição
  const finishEditChecklistItem = () => {
    setEditingChecklistIndex(null);
  };

  // Estado do filtro de visualização
  const [checklistFilter, setChecklistFilter] = useState('todos');

  // Filtrar e agrupar checklist
  const filteredChecklist = useMemo(() => {
    let items = formData.checklist_base.map((item, index) => ({ ...item, originalIndex: index }));

    if (checklistFilter === 'especificacoes') {
      items = items.filter(i => i.tipo_item === 'especificacao');
    } else if (checklistFilter === 'produtos') {
      items = items.filter(i => i.tipo_item === 'produto' || (!i.tipo_item));
    } else if (checklistFilter === 'servicos') {
      items = items.filter(i => i.tipo_item === 'servico');
    } else if (checklistFilter === 'categorias') {
      // Agrupar por categoria
      const groups = {};
      items.forEach(item => {
        const key = item.categoria || 'Outros';
        if (!groups[key]) groups[key] = [];
        groups[key].push(item);
      });
      return { grouped: true, groups };
    }

    return { grouped: false, items };
  }, [formData.checklist_base, checklistFilter]);

  // Selecionar/Desmarcar todos visíveis
  const toggleAllVisible = (selected) => {
    const updated = [...formData.checklist_base];
    const visibleItems = filteredChecklist.grouped
      ? Object.values(filteredChecklist.groups).flat()
      : filteredChecklist.items;
    visibleItems.forEach(item => {
      updated[item.originalIndex] = { ...updated[item.originalIndex], selected };
    });
    setFormData({ ...formData, checklist_base: updated });
  };

  // Selecionar/Desmarcar todos de uma categoria
  const toggleCategorySelection = (categoryItems, selected) => {
    const updated = [...formData.checklist_base];
    categoryItems.forEach(item => {
      updated[item.originalIndex] = { ...updated[item.originalIndex], selected };
    });
    setFormData({ ...formData, checklist_base: updated });
  };

  // Selecionar/Desmarcar item individual
  const toggleItemSelection = (originalIndex) => {
    const updated = [...formData.checklist_base];
    updated[originalIndex] = { ...updated[originalIndex], selected: !updated[originalIndex].selected };
    setFormData({ ...formData, checklist_base: updated });
  };

  // Contadores
  const selectedCount = formData.checklist_base.filter(i => i.selected !== false).length;
  const visibleItems = filteredChecklist.grouped
    ? Object.values(filteredChecklist.groups).flat()
    : (filteredChecklist.items || []);
  const visibleSelectedCount = visibleItems.filter(i => formData.checklist_base[i.originalIndex]?.selected !== false).length;
  const allVisibleSelected = visibleItems.length > 0 && visibleSelectedCount === visibleItems.length;

  // Renderiza uma linha do checklist
  const renderChecklistItem = (item, showType, allowDrag = true) => (
    <div
      key={item.originalIndex}
      className={`group ${dragOverIndex === item.originalIndex ? 'bg-blue-50 ring-2 ring-blue-300 ring-inset' : ''}`}
      draggable={allowDrag && editingChecklistIndex !== item.originalIndex}
      onDragStart={(e) => allowDrag && handleDragStart(e, item.originalIndex)}
      onDragOver={(e) => allowDrag && handleDragOver(e, item.originalIndex)}
      onDragLeave={allowDrag ? handleDragLeave : undefined}
      onDrop={(e) => allowDrag && handleDrop(e, item.originalIndex)}
      data-testid={`checklist-row-${item.originalIndex}`}
    >
      {editingChecklistIndex === item.originalIndex ? (
        <div className="p-2 bg-yellow-50 border-b border-yellow-300 space-y-2">
          <div>
            <Label className="text-xs">Descrição</Label>
            <Input value={formData.checklist_base[item.originalIndex].descricao}
              onChange={(e) => updateChecklistItem(item.originalIndex, 'descricao', e.target.value)}
              className="h-8 text-sm" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Categoria</Label>
              <select value={formData.checklist_base[item.originalIndex].categoria}
                onChange={(e) => updateChecklistItem(item.originalIndex, 'categoria', e.target.value)}
                className="w-full h-8 px-2 rounded-md border border-input bg-background text-sm">
                {categorias.map(cat => (<option key={cat} value={cat}>{cat}</option>))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Tipo</Label>
              <select value={formData.checklist_base[item.originalIndex].tipo_item || 'produto'}
                onChange={(e) => updateChecklistItem(item.originalIndex, 'tipo_item', e.target.value)}
                className="w-full h-8 px-2 rounded-md border border-input bg-background text-sm">
                <option value="produto">Produto</option>
                <option value="especificacao">Especificação</option>
                <option value="servico">Serviço</option>
              </select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Observação</Label>
            <Input value={formData.checklist_base[item.originalIndex].observacao || ''}
              onChange={(e) => updateChecklistItem(item.originalIndex, 'observacao', e.target.value)}
              className="h-8 text-sm" placeholder="Opcional" />
          </div>
          <div className="flex justify-end">
            <Button type="button" size="sm" onClick={finishEditChecklistItem}>
              <Check className="h-3 w-3 mr-1" />Concluir
            </Button>
          </div>
        </div>
      ) : (
        <div className={`flex items-center gap-2 px-3 py-1.5 border-b border-gray-100 hover:bg-gray-50 ${item.selected === false ? 'opacity-40' : ''}`}>
          {allowDrag && (
            <GripVertical
              className="h-4 w-4 text-gray-300 group-hover:text-gray-500 cursor-grab active:cursor-grabbing shrink-0"
              data-testid={`checklist-drag-handle-${item.originalIndex}`}
            />
          )}
          <input type="checkbox" checked={item.selected !== false}
            onChange={() => toggleItemSelection(item.originalIndex)}
            className="h-4 w-4 rounded border-gray-300 shrink-0" />
          <span className="flex-1 text-sm">{item.descricao}</span>
          {showType && (
            <Badge variant="outline" className="text-xs shrink-0">
              {item.tipo_item === 'servico' ? 'Serviço' : item.tipo_item === 'especificacao' ? 'Espec.' : 'Produto'}
            </Badge>
          )}
          {item.categoria && (
            <span className="text-xs text-gray-400 shrink-0">{item.categoria}</span>
          )}
          <div className="flex gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button type="button" variant="ghost" size="icon" className="h-6 w-6"
              onClick={() => startEditChecklistItem(item.originalIndex)}>
              <Edit className="h-3 w-3 text-blue-500" />
            </Button>
            <Button type="button" variant="ghost" size="icon" className="h-6 w-6"
              onClick={() => removeChecklistItem(item.originalIndex)}>
              <Trash2 className="h-3 w-3 text-red-500" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  // Toggle fase aplicável - corrigido para evitar loop
  const handleFaseToggle = (faseId) => {
    setFormData(prev => {
      const isSelected = prev.fases_aplicaveis.includes(faseId);
      return {
        ...prev,
        fases_aplicaveis: isSelected
          ? prev.fases_aplicaveis.filter(id => id !== faseId)
          : [...prev.fases_aplicaveis, faseId]
      };
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <TitleWithHelp
            title="Catálogo de Modelos"
            description="Define os tipos de embarcação que sua empresa fabrica. Cada modelo tem características e fases de produção específicas."
            connections={["Projetos"]}
          >
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Anchor className="h-6 w-6 text-blue-600" />
              Modelos de Embarcação
            </h1>
          </TitleWithHelp>
          <p className="text-gray-500 mt-1">Catálogo de modelos que a fábrica produz</p>
        </div>
        <div className="flex items-center gap-3">
          <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
          <Button onClick={handleOpenNew} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="mr-2 h-4 w-4" />
            Novo Modelo
          </Button>
        </div>
      </div>

      {/* Lista de Modelos */}
      {models.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Ship className="h-12 w-12 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900">Nenhum modelo cadastrado</h3>
            <p className="text-gray-500 mt-1">Comece criando seu primeiro modelo de embarcação</p>
            <Button onClick={handleOpenNew} className="mt-4">
              <Plus className="mr-2 h-4 w-4" />
              Criar Modelo
            </Button>
          </CardContent>
        </Card>
      ) : viewMode === 'list' ? (
        <Card data-testid="modelos-list-view">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50/80">
                  <th className="text-left p-3 font-medium text-gray-600">Modelo</th>
                  <th className="text-left p-3 font-medium text-gray-600">Código</th>
                  <th className="text-left p-3 font-medium text-gray-600">Descrição</th>
                  <th className="text-left p-3 font-medium text-gray-600">Horas Produção</th>
                  <th className="text-left p-3 font-medium text-gray-600">Checklist</th>
                  <th className="text-right p-3 font-medium text-gray-600">Ações</th>
                </tr>
              </thead>
              <tbody>
                {models.map(model => (
                  <tr key={model.id} className="border-b hover:bg-blue-50/40 transition-colors group" data-testid={`modelo-row-${model.id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Ship className="h-4 w-4 text-blue-600 flex-shrink-0" />
                        <span className="font-medium text-gray-900">{model.nome}</span>
                      </div>
                    </td>
                    <td className="p-3"><Badge variant="outline" className="text-xs">{model.codigo}</Badge></td>
                    <td className="p-3 text-gray-600 max-w-[250px] truncate">{model.descricao || '—'}</td>
                    <td className="p-3 text-gray-600">{model.horas_estimadas ? `${model.horas_estimadas}h` : '—'}</td>
                    <td className="p-3 text-gray-600">{model.checklist_base?.length || 0} itens</td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleDuplicate(model)} title="Duplicar" data-testid={`duplicate-modelo-${model.id}`}>
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleEdit(model)} title="Editar">
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => setDeleteModelId(model.id)} title="Excluir">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {models.map((model) => (
            <Card key={model.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <Ship className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{model.nome}</CardTitle>
                      <Badge variant="outline" className="mt-1">{model.codigo}</Badge>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleDuplicate(model)} title="Duplicar" data-testid={`duplicate-modelo-grid-${model.id}`}>
                      <Copy className="h-4 w-4 text-gray-500" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(model)} title="Editar">
                      <Edit className="h-4 w-4 text-gray-500" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setDeleteModelId(model.id)} title="Excluir">
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {model.descricao && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{model.descricao}</p>
                )}
                {model.horas_estimadas > 0 && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                    <Clock className="h-4 w-4" />
                    <span>{model.horas_estimadas}h de produção</span>
                  </div>
                )}
                <div className="mt-3 pt-3 border-t flex items-center gap-2 text-xs text-gray-500">
                  <ClipboardList className="h-3 w-3" />
                  <span>{model.checklist_base?.length || 0} itens no checklist</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog de Criação/Edição */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ship className="h-5 w-5" />
              {editingModel ? 'Editar Modelo de Embarcação' : 'Novo Modelo de Embarcação'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="geral">Geral</TabsTrigger>
                <TabsTrigger value="checklist">Checklist ({formData.checklist_base.length})</TabsTrigger>
                <TabsTrigger value="comercial" className="gap-1"><DollarSign className="w-3.5 h-3.5" />Comercial</TabsTrigger>
                <TabsTrigger value="fases">Fases</TabsTrigger>
              </TabsList>

              {/* Aba Geral */}
              <TabsContent value="geral" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="codigo">Código/SKU *</Label>
                    <Input
                      id="codigo"
                      value={formData.codigo}
                      onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toUpperCase() })}
                      placeholder="T800PRO"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="nome">Nome *</Label>
                    <Input
                      id="nome"
                      value={formData.nome}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                      placeholder="T800 PRO - Semirrigida 8m"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="descricao">Descrição</Label>
                  <Textarea
                    id="descricao"
                    value={formData.descricao}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    placeholder="Descrição detalhada do modelo..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="horas_estimadas">Horas Estimadas de Produção</Label>
                  <Input
                    id="horas_estimadas"
                    type="number"
                    value={formData.horas_estimadas}
                    onChange={(e) => setFormData({ ...formData, horas_estimadas: e.target.value })}
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500">Tempo estimado para produção completa desta embarcação</p>
                </div>

                <div className="space-y-2">
                  <Label>Documentação do Modelo</Label>
                  <AnexosErrorBoundary>
                    <AnexosManager
                      anexos={formData.anexos || []}
                      onChange={(novos) => setFormData(f => ({ ...f, anexos: novos }))}
                      maxFiles={20}
                      maxSizeMB={50}
                      label=""
                      modulo="vessel_models"
                      entidadeId={editingModel?.id || 'novo'}
                      categoria="documentacao"
                    />
                  </AnexosErrorBoundary>
                  <p className="text-xs text-gray-500">Manuais, padrões, especificações técnicas, fotos de referência (PDFs, imagens, planilhas...)</p>
                </div>
              </TabsContent>

              {/* Aba Checklist */}
              <TabsContent value="checklist" className="mt-4">
                <div className="flex justify-between items-center mb-3">
                  <p className="text-sm text-gray-500">
                    {selectedCount} de {formData.checklist_base.length} itens selecionados
                    <span className="ml-2 text-xs text-blue-600">— arraste pelo ícone <GripVertical className="inline h-3 w-3 -mt-0.5" /> para reordenar</span>
                  </p>
                  {!newChecklistItem && (
                    <Button type="button" variant="outline" size="sm" onClick={startAddChecklistItem}>
                      <Plus className="h-4 w-4 mr-1" />
                      Adicionar Item
                    </Button>
                  )}
                </div>

                {/* Abas de filtro */}
                <div className="flex gap-1 mb-3 border-b pb-2 overflow-x-auto">
                  {[
                    { key: 'todos', label: 'Todos os itens' },
                    { key: 'categorias', label: 'Categorias' },
                    { key: 'especificacoes', label: 'Especificações' },
                    { key: 'produtos', label: 'Produtos' },
                    { key: 'servicos', label: 'Serviços' },
                  ].map(tab => (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setChecklistFilter(tab.key)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap transition-colors ${
                        checklistFilter === tab.key
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {tab.label}
                      {tab.key !== 'todos' && tab.key !== 'categorias' && (
                        <span className="ml-1 opacity-75">
                          ({formData.checklist_base.filter(i =>
                            tab.key === 'especificacoes' ? i.tipo_item === 'especificacao' :
                            tab.key === 'produtos' ? (i.tipo_item === 'produto' || !i.tipo_item) :
                            i.tipo_item === 'servico'
                          ).length})
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                {/* Formulário de novo item */}
                {newChecklistItem && (
                  <div className="mb-4 p-4 border-2 border-blue-500 rounded-lg bg-blue-50">
                    <h4 className="font-medium text-blue-700 mb-3">Novo Item do Checklist</h4>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm">Descrição do Item *</Label>
                        <Input value={newChecklistItem.descricao}
                          onChange={(e) => setNewChecklistItem({ ...newChecklistItem, descricao: e.target.value })}
                          placeholder="Ex: Comprimento total incluindo plataforma" className="mt-1" autoFocus />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm">Categoria</Label>
                          {showNewCategoria ? (
                            <div className="flex gap-2 mt-1">
                              <Input value={newCategoria} onChange={(e) => setNewCategoria(e.target.value)}
                                placeholder="Nome da nova categoria" autoFocus
                                onKeyDown={(e) => e.key === 'Enter' && handleAddNewCategoria()} />
                              <Button type="button" size="sm" onClick={handleAddNewCategoria}><Check className="h-4 w-4" /></Button>
                              <Button type="button" size="sm" variant="ghost" onClick={() => { setShowNewCategoria(false); setNewCategoria(''); }}><X className="h-4 w-4" /></Button>
                            </div>
                          ) : (
                            <div className="flex gap-2 mt-1">
                              <select value={newChecklistItem.categoria}
                                onChange={(e) => setNewChecklistItem({ ...newChecklistItem, categoria: e.target.value })}
                                className="flex-1 h-10 px-3 rounded-md border border-input bg-background text-sm">
                                {categorias.map(cat => (<option key={cat} value={cat}>{cat}</option>))}
                              </select>
                              <Button type="button" size="sm" variant="outline" onClick={() => setShowNewCategoria(true)}>
                                <Plus className="h-4 w-4 mr-1" />Nova
                              </Button>
                            </div>
                          )}
                        </div>
                        <div>
                          <Label className="text-sm">Tipo do Item</Label>
                          <select value={newChecklistItem.tipo_item || 'produto'}
                            onChange={(e) => setNewChecklistItem({ ...newChecklistItem, tipo_item: e.target.value })}
                            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm mt-1">
                            <option value="produto">Produto</option>
                            <option value="especificacao">Especificação</option>
                            <option value="servico">Serviço</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm">Observação (opcional)</Label>
                        <Input value={newChecklistItem.observacao}
                          onChange={(e) => setNewChecklistItem({ ...newChecklistItem, observacao: e.target.value })}
                          placeholder="Observação padrão para este item" className="mt-1" />
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-md">
                        <input type="checkbox" id="is_item_padrao"
                          checked={newChecklistItem.is_item_padrao !== false}
                          onChange={(e) => setNewChecklistItem({ ...newChecklistItem, is_item_padrao: e.target.checked })}
                          className="h-4 w-4 rounded border-gray-300" />
                        <label htmlFor="is_item_padrao" className="text-sm text-blue-800 cursor-pointer">
                          Incluir este item em futuras embarcações deste modelo
                        </label>
                      </div>
                      <div className="flex justify-end gap-2 pt-2">
                        <Button type="button" variant="ghost" onClick={cancelAddChecklistItem}>Cancelar</Button>
                        <Button type="button" onClick={confirmAddChecklistItem} className="bg-blue-600 hover:bg-blue-700">
                          <Check className="h-4 w-4 mr-1" />Adicionar Item
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Header selecionar/desmarcar */}
                <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border rounded-t-lg">
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={allVisibleSelected}
                      ref={el => { if (el) el.indeterminate = visibleSelectedCount > 0 && !allVisibleSelected; }}
                      onChange={() => toggleAllVisible(!allVisibleSelected)}
                      className="h-4 w-4 rounded border-gray-300" />
                    <span className="text-sm font-medium text-gray-700">
                      {checklistFilter === 'todos' ? 'Todos os itens' :
                       checklistFilter === 'categorias' ? 'Por Categoria' :
                       checklistFilter === 'especificacoes' ? 'Especificações' :
                       checklistFilter === 'produtos' ? 'Produtos' : 'Serviços'}
                    </span>
                    <Badge variant="secondary" className="text-xs">{visibleSelectedCount}/{visibleItems.length}</Badge>
                  </div>
                  <button type="button" onClick={() => toggleAllVisible(!allVisibleSelected)} className="text-xs text-gray-500 hover:text-gray-800">
                    {allVisibleSelected ? 'Desmarcar todos' : 'Selecionar todos'}
                  </button>
                </div>

                {/* Lista de itens */}
                <div className="max-h-80 overflow-y-auto border border-t-0 rounded-b-lg">
                  {filteredChecklist.grouped ? (
                    Object.entries(filteredChecklist.groups).sort((a, b) => a[0].localeCompare(b[0])).map(([catName, catItems]) => {
                      const catSelCount = catItems.filter(i => formData.checklist_base[i.originalIndex]?.selected !== false).length;
                      const catAll = catSelCount === catItems.length;
                      return (
                        <div key={catName}>
                          <div className="flex items-center justify-between px-3 py-1.5 bg-gray-100 border-b sticky top-0 z-10">
                            <div className="flex items-center gap-2">
                              <input type="checkbox" checked={catAll}
                                ref={el => { if (el) el.indeterminate = catSelCount > 0 && !catAll; }}
                                onChange={() => toggleCategorySelection(catItems, !catAll)}
                                className="h-3.5 w-3.5 rounded border-gray-300" />
                              <span className="text-xs font-semibold text-gray-600">{catName}</span>
                              <span className="text-xs text-gray-400">{catSelCount}/{catItems.length}</span>
                            </div>
                          </div>
                          {catItems.map((item) => renderChecklistItem(item, true))}
                        </div>
                      );
                    })
                  ) : (
                    (filteredChecklist.items || []).map((item) => renderChecklistItem(item, checklistFilter === 'todos'))
                  )}
                  {visibleItems.length === 0 && (
                    <div className="text-center py-6 text-gray-400 text-sm">Nenhum item nesta categoria</div>
                  )}
                </div>
              </TabsContent>

              {/* Aba Comercial */}
              <TabsContent value="comercial" className="mt-4 space-y-5">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                  Configure aqui os dados comerciais para o gerador de propostas. Estes dados servem como base — o vendedor pode personalizar na proposta.
                </div>

                {/* Preço Base */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-semibold">Preço Base (R$)</Label>
                    <p className="text-[10px] text-slate-500 mb-1">Valor base da embarcação sem opcionais</p>
                    <Input
                      type="number" step="0.01"
                      value={formData.config_comercial.preco_base || ''}
                      onChange={e => setFormData(f => ({ ...f, config_comercial: { ...f.config_comercial, preco_base: parseFloat(e.target.value) || 0 } }))}
                      placeholder="0,00"
                      data-testid="input-preco-base"
                    />
                  </div>
                  <div className="flex items-end">
                    {formData.config_comercial.preco_base > 0 && (
                      <Badge variant="outline" className="text-green-600 border-green-300 text-sm px-3 py-1">
                        {formatCurrency(formData.config_comercial.preco_base)}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Texto de Apresentação */}
                <div>
                  <Label className="text-xs font-semibold">Texto de Apresentação (template)</Label>
                  <p className="text-[10px] text-slate-500 mb-1">Texto padrão que aparecerá nas propostas deste modelo</p>
                  <Textarea
                    value={formData.config_comercial.texto_apresentacao || ''}
                    onChange={e => setFormData(f => ({ ...f, config_comercial: { ...f.config_comercial, texto_apresentacao: e.target.value } }))}
                    rows={3}
                    placeholder="Ex: Temos o prazer de apresentar o modelo X, embarcação projetada para..."
                  />
                </div>

                {/* Itens Padrão */}
                <div className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700">Itens Padrão</h4>
                      <p className="text-[10px] text-slate-500">Incluídos no preço base. Aparecem na proposta como "incluso"</p>
                    </div>
                    <Button type="button" variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => {
                      setFormData(f => ({
                        ...f,
                        config_comercial: {
                          ...f.config_comercial,
                          itens_serie: [...f.config_comercial.itens_serie, { id: crypto.randomUUID(), grupo: '', descricao: '', quantidade: 1, observacao: '' }]
                        }
                      }));
                    }}>
                      <Plus className="w-3 h-3" /> Adicionar
                    </Button>
                  </div>
                  {formData.config_comercial.itens_serie.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-3">Nenhum item padrão cadastrado</p>
                  ) : (
                    <div className="space-y-2">
                      {formData.config_comercial.itens_serie.map((item, idx) => (
                        <div key={item.id} className="flex items-center gap-2 bg-slate-50 rounded p-2">
                          <Input
                            value={item.grupo}
                            onChange={e => {
                              const updated = [...formData.config_comercial.itens_serie];
                              updated[idx] = { ...updated[idx], grupo: e.target.value };
                              setFormData(f => ({ ...f, config_comercial: { ...f.config_comercial, itens_serie: updated } }));
                            }}
                            placeholder="Grupo (ex: Propulsão)"
                            className="h-7 text-xs w-32"
                          />
                          <Input
                            value={item.descricao}
                            onChange={e => {
                              const updated = [...formData.config_comercial.itens_serie];
                              updated[idx] = { ...updated[idx], descricao: e.target.value };
                              setFormData(f => ({ ...f, config_comercial: { ...f.config_comercial, itens_serie: updated } }));
                            }}
                            placeholder="Descrição (ex: Motor Mercury 150HP)"
                            className="h-7 text-xs flex-1"
                          />
                          <Input
                            type="number" min="1"
                            value={item.quantidade}
                            onChange={e => {
                              const updated = [...formData.config_comercial.itens_serie];
                              updated[idx] = { ...updated[idx], quantidade: parseInt(e.target.value) || 1 };
                              setFormData(f => ({ ...f, config_comercial: { ...f.config_comercial, itens_serie: updated } }));
                            }}
                            className="h-7 text-xs w-16"
                            placeholder="Qtd"
                          />
                          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400"
                            onClick={() => {
                              setFormData(f => ({
                                ...f,
                                config_comercial: { ...f.config_comercial, itens_serie: f.config_comercial.itens_serie.filter((_, i) => i !== idx) }
                              }));
                            }}>
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Opcionais */}
                <div className="border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="text-sm font-semibold text-blue-700">Opcionais</h4>
                      <p className="text-[10px] text-slate-500">Itens extras que o cliente pode adicionar (preço adicional)</p>
                    </div>
                    <Button type="button" variant="outline" size="sm" className="h-7 text-xs gap-1 border-blue-200 text-blue-600" onClick={() => {
                      setFormData(f => ({
                        ...f,
                        config_comercial: {
                          ...f.config_comercial,
                          opcionais: [...f.config_comercial.opcionais, { id: crypto.randomUUID(), grupo: '', descricao: '', preco: 0, observacao: '' }]
                        }
                      }));
                    }}>
                      <Plus className="w-3 h-3" /> Adicionar
                    </Button>
                  </div>
                  {formData.config_comercial.opcionais.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-3">Nenhum opcional cadastrado</p>
                  ) : (
                    <div className="space-y-2">
                      {formData.config_comercial.opcionais.map((item, idx) => (
                        <div key={item.id} className="flex items-center gap-2 bg-blue-50/50 rounded p-2">
                          <Input
                            value={item.grupo}
                            onChange={e => {
                              const updated = [...formData.config_comercial.opcionais];
                              updated[idx] = { ...updated[idx], grupo: e.target.value };
                              setFormData(f => ({ ...f, config_comercial: { ...f.config_comercial, opcionais: updated } }));
                            }}
                            placeholder="Grupo"
                            className="h-7 text-xs w-32"
                          />
                          <Input
                            value={item.descricao}
                            onChange={e => {
                              const updated = [...formData.config_comercial.opcionais];
                              updated[idx] = { ...updated[idx], descricao: e.target.value };
                              setFormData(f => ({ ...f, config_comercial: { ...f.config_comercial, opcionais: updated } }));
                            }}
                            placeholder="Descrição (ex: Pacote LED Premium)"
                            className="h-7 text-xs flex-1"
                          />
                          <div className="relative">
                            <span className="absolute left-2 top-1 text-[10px] text-slate-400">R$</span>
                            <Input
                              type="number" step="0.01"
                              value={item.preco || ''}
                              onChange={e => {
                                const updated = [...formData.config_comercial.opcionais];
                                updated[idx] = { ...updated[idx], preco: parseFloat(e.target.value) || 0 };
                                setFormData(f => ({ ...f, config_comercial: { ...f.config_comercial, opcionais: updated } }));
                              }}
                              className="h-7 text-xs w-28 pl-7"
                              placeholder="Preço"
                            />
                          </div>
                          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400"
                            onClick={() => {
                              setFormData(f => ({
                                ...f,
                                config_comercial: { ...f.config_comercial, opcionais: f.config_comercial.opcionais.filter((_, i) => i !== idx) }
                              }));
                            }}>
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Upgrades */}
                <div className="border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="text-sm font-semibold text-amber-700">Upgrades</h4>
                      <p className="text-[10px] text-slate-500">Substituem um item padrão por versão superior (preço adicional = diferença)</p>
                    </div>
                    <Button type="button" variant="outline" size="sm" className="h-7 text-xs gap-1 border-amber-200 text-amber-600" onClick={() => {
                      setFormData(f => ({
                        ...f,
                        config_comercial: {
                          ...f.config_comercial,
                          upgrades: [...f.config_comercial.upgrades, { id: crypto.randomUUID(), grupo: '', descricao: '', substitui_descricao: '', preco_adicional: 0, observacao: '' }]
                        }
                      }));
                    }}>
                      <Plus className="w-3 h-3" /> Adicionar
                    </Button>
                  </div>
                  {formData.config_comercial.upgrades.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-3">Nenhum upgrade cadastrado</p>
                  ) : (
                    <div className="space-y-2">
                      {formData.config_comercial.upgrades.map((item, idx) => (
                        <div key={item.id} className="bg-amber-50/50 rounded p-2 space-y-1">
                          <div className="flex items-center gap-2">
                            <Input
                              value={item.grupo}
                              onChange={e => {
                                const updated = [...formData.config_comercial.upgrades];
                                updated[idx] = { ...updated[idx], grupo: e.target.value };
                                setFormData(f => ({ ...f, config_comercial: { ...f.config_comercial, upgrades: updated } }));
                              }}
                              placeholder="Grupo"
                              className="h-7 text-xs w-32"
                            />
                            <Input
                              value={item.descricao}
                              onChange={e => {
                                const updated = [...formData.config_comercial.upgrades];
                                updated[idx] = { ...updated[idx], descricao: e.target.value };
                                setFormData(f => ({ ...f, config_comercial: { ...f.config_comercial, upgrades: updated } }));
                              }}
                              placeholder="Upgrade (ex: Motor Yamaha 200HP)"
                              className="h-7 text-xs flex-1"
                            />
                            <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400"
                              onClick={() => {
                                setFormData(f => ({
                                  ...f,
                                  config_comercial: { ...f.config_comercial, upgrades: f.config_comercial.upgrades.filter((_, i) => i !== idx) }
                                }));
                              }}>
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                          <div className="flex items-center gap-2 pl-1">
                            <span className="text-[10px] text-slate-500 whitespace-nowrap">Substitui:</span>
                            <Input
                              value={item.substitui_descricao}
                              onChange={e => {
                                const updated = [...formData.config_comercial.upgrades];
                                updated[idx] = { ...updated[idx], substitui_descricao: e.target.value };
                                setFormData(f => ({ ...f, config_comercial: { ...f.config_comercial, upgrades: updated } }));
                              }}
                              placeholder="Item padrão substituído (ex: Motor Mercury 150HP)"
                              className="h-7 text-xs flex-1"
                            />
                            <div className="relative">
                              <span className="absolute left-2 top-1 text-[10px] text-amber-500">+R$</span>
                              <Input
                                type="number" step="0.01"
                                value={item.preco_adicional || ''}
                                onChange={e => {
                                  const updated = [...formData.config_comercial.upgrades];
                                  updated[idx] = { ...updated[idx], preco_adicional: parseFloat(e.target.value) || 0 };
                                  setFormData(f => ({ ...f, config_comercial: { ...f.config_comercial, upgrades: updated } }));
                                }}
                                className="h-7 text-xs w-28 pl-8"
                                placeholder="Diferença"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Dimensões Gerais */}
                <div>
                  <Label className="text-xs font-semibold">Dimensões Gerais</Label>
                  <p className="text-[10px] text-slate-500 mb-1">Especificações de dimensões que o vendedor pode personalizar na proposta</p>
                  <Textarea
                    value={formData.config_comercial.dimensoes_gerais || ''}
                    onChange={e => setFormData(f => ({ ...f, config_comercial: { ...f.config_comercial, dimensoes_gerais: e.target.value } }))}
                    rows={3}
                    placeholder="Ex: Comprimento: 22 pés / Boca: 2,50m / Calado: 0,45m / Capacidade: 10 pessoas"
                  />
                </div>

                {/* Termos e Condições */}
                <div>
                  <Label className="text-xs font-semibold">Termos e Condições (template)</Label>
                  <p className="text-[10px] text-slate-500 mb-1">Texto padrão de termos, usado na proposta como base</p>
                  <Textarea
                    value={formData.config_comercial.termos_condicoes || ''}
                    onChange={e => setFormData(f => ({ ...f, config_comercial: { ...f.config_comercial, termos_condicoes: e.target.value } }))}
                    rows={3}
                    placeholder="Ex: A garantia cobre defeitos de fabricação. Não inclui mau uso ou desgaste natural..."
                  />
                </div>

                {/* Kit Montagem */}
                <div>
                  <Label className="text-xs font-semibold">Kit Montagem (template)</Label>
                  <p className="text-[10px] text-slate-500 mb-1">Descrição do kit de montagem incluído, quando aplicável</p>
                  <Textarea
                    value={formData.config_comercial.kit_montagem || ''}
                    onChange={e => setFormData(f => ({ ...f, config_comercial: { ...f.config_comercial, kit_montagem: e.target.value } }))}
                    rows={3}
                    placeholder="Ex: Kit inclui: casco, flutuadores, motor, caixa de direção, cabo de comando..."
                  />
                </div>

                {/* Imagens da Proposta */}
                <div className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700">Imagens da Proposta</h4>
                      <p className="text-[10px] text-slate-500">Até 4 imagens de alta qualidade. O gerador de propostas puxará daqui.</p>
                    </div>
                    {(formData.config_comercial.imagens_proposta || []).length < 4 && (
                      <label className="inline-flex items-center gap-1 cursor-pointer bg-blue-50 border border-blue-200 rounded px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-100 transition-colors">
                        <Plus className="w-3 h-3" /> Upload
                        <input
                          type="file" accept="image/*" className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            if (file.size > 10 * 1024 * 1024) { toast.error('Imagem deve ter no máximo 10MB'); return; }
                            try {
                              const fd = new FormData();
                              fd.append('file', file);
                              const res = await api.post(`/upload/image?modulo=modelos&entidade_id=${editingModel?.id || 'novo'}`, fd, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              let url = res.data.url || res.data.file_url || res.data.image_url;
                              if (url) {
                                // Convert relative URL to absolute
                                if (url.startsWith('/api/')) {
                                  url = `${process.env.REACT_APP_BACKEND_URL}${url}`;
                                } else if (url.startsWith('/')) {
                                  url = `${process.env.REACT_APP_BACKEND_URL}${url}`;
                                }
                                setFormData(f => ({
                                  ...f,
                                  config_comercial: {
                                    ...f.config_comercial,
                                    imagens_proposta: [...(f.config_comercial.imagens_proposta || []), { url, legenda: file.name.replace(/\.[^.]+$/, '') }]
                                  }
                                }));
                                toast.success('Imagem enviada');
                              }
                            } catch (err) { toast.error('Erro ao enviar imagem'); }
                            e.target.value = '';
                          }}
                        />
                      </label>
                    )}
                  </div>
                  {(formData.config_comercial.imagens_proposta || []).length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-4">Nenhuma imagem cadastrada</p>
                  ) : (
                    <div className="grid grid-cols-5 gap-2">
                      {(formData.config_comercial.imagens_proposta || []).map((img, idx) => (
                        <div key={img.url || img.legenda || `img-${idx}`} className="relative group rounded-lg overflow-hidden border border-slate-200">
                          <img src={img.url} alt={img.legenda || `Imagem ${idx + 1}`} className="w-full h-24 object-cover" />
                          <Input
                            value={img.legenda || ''}
                            onChange={e => {
                              const updated = [...formData.config_comercial.imagens_proposta];
                              updated[idx] = { ...updated[idx], legenda: e.target.value };
                              setFormData(f => ({ ...f, config_comercial: { ...f.config_comercial, imagens_proposta: updated } }));
                            }}
                            placeholder="Legenda"
                            className="h-6 text-[10px] rounded-none border-0 border-t"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setFormData(f => ({
                                ...f,
                                config_comercial: { ...f.config_comercial, imagens_proposta: f.config_comercial.imagens_proposta.filter((_, i) => i !== idx) }
                              }));
                            }}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
              <TabsContent value="fases" className="mt-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-gray-500">
                      Selecione quais fases do processo de produção se aplicam a este modelo.
                    </p>
                    <HelpTooltip 
                      title="Conexão com Processos"
                      description="Selecione quais fases de produção (cadastradas em Processos) se aplicam a este modelo. Gera o cronograma automaticamente ao criar projeto."
                      connections={["Processos", "Cronograma"]}
                      iconSize={14}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const todasFasesIds = processTemplates.map(f => f.id);
                      const todasSelecionadas = formData.fases_aplicaveis.length === todasFasesIds.length;
                      setFormData(prev => ({
                        ...prev,
                        fases_aplicaveis: todasSelecionadas ? [] : todasFasesIds
                      }));
                    }}
                  >
                    {formData.fases_aplicaveis.length === processTemplates.length ? 'Desmarcar Todas' : 'Selecionar Todas'}
                  </Button>
                </div>

                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {processTemplates.map((fase) => {
                    const isSelected = formData.fases_aplicaveis.includes(fase.id);
                    return (
                      <div
                        key={fase.id}
                        className={`p-3 border rounded-md cursor-pointer transition-colors ${
                          isSelected ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                        }`}
                        onClick={() => handleFaseToggle(fase.id)}
                      >
                        <div className="flex items-center gap-3">
                          {/* Checkbox visual simples sem Radix */}
                          <div className={`h-4 w-4 rounded-sm border flex items-center justify-center shrink-0 ${
                            isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                          }`}>
                            {isSelected && <Check className="h-3 w-3 text-white" />}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{fase.name}</span>
                              <Badge variant="outline" className="text-xs">
                                Etapa {fase.etapa_macro}
                              </Badge>
                            </div>
                            {fase.description && (
                              <p className="text-xs text-gray-500 mt-1">{fase.description}</p>
                            )}
                          </div>
                          {fase.duration_hours > 0 && (
                            <span className="text-xs text-gray-400">{fase.duration_hours}h</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {formData.fases_aplicaveis.length > 0 && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-md">
                    <p className="text-sm text-blue-700">
                      <strong>{formData.fases_aplicaveis.length}</strong> fases selecionadas
                    </p>
                  </div>
                )}
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                {editingModel ? 'Salvar Alterações' : 'Criar Modelo'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={!!deleteModelId} onOpenChange={() => setDeleteModelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este modelo de embarcação? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VesselModels;
